<script setup lang="ts">
import { ref } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import EventsEventCard from '@/ui-kit/events/EventCard.vue'
import EventsFilters from '@/ui-kit/events/Filters.vue'
import EventsReviewModal from '@/ui-kit/events/AddReviewModal.vue'
import EventsUsefulLinksModal from '@/ui-kit/events/UsefulLinksModal.vue'
import type { EventCardEvent } from '@/ui-kit/events/types'

type EventsMode = 'next' | 'previous' | 'my'

interface EventsIndexContext {
  initial_mode?: EventsMode
}

const ctx = usePageContext<EventsIndexContext>()

const mode = ref<EventsMode>(ctx.initial_mode ?? 'next')
const fetching = ref(true)
const errored = ref(false)
const events = ref<EventCardEvent[]>([])
const usefulLinks = ref<Array<{ name: string; url: string }> | null>(null)

function handleEventsUpdated(value: EventCardEvent[]) {
  events.value = value
}

function handleLikeChanged(index: number, value: boolean) {
  const event = events.value[index]
  if (!event) return
  event.me_liked = value
}

function handleUsefulLinks(event: EventCardEvent) {
  usefulLinks.value = event.useful_links ?? null
}
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="teacher" rules-url="/website/documents/oferta" />

    <div v-cloak class="content events-index">
      <v-notifications position="bottom right" />

      <h1 class="header">Мероприятия</h1>
      <EventsFilters
        v-model:mode="mode"
        class="mt-2 mt-d-3"
        @events-updated="handleEventsUpdated"
        @fetch-start="
          () => {
            fetching = true
            errored = false
          }
        "
        @fetch-end="
          () => {
            fetching = false
          }
        "
        @error="
          () => {
            errored = true
          }
        "
      />

      <div v-if="errored" class="events-index__loading events-index__loading_errored">
        <v-icon size="64">mdi-alert-circle-outline</v-icon>
        Произошла ошибка. Попробуйте обновить страницу.
      </div>
      <div v-else-if="fetching" class="events-index__loading">
        <v-icon size="64" class="mdi-spin">mdi-timer-sand</v-icon>
        Загрузка...
      </div>
      <div v-else-if="!events.length" class="events-index__loading events-index__loading_no-results">
        <v-icon size="64">mdi-calendar-question</v-icon>
        Ближайшие мероприятия не найдены.
      </div>

      <div v-else class="events-index__content-grid" :class="{ 'events-index__content-grid_list': mode === 'my' }">
        <EventsEventCard
          v-for="(event, i) in events"
          :key="event.id"
          :size="mode === 'my' ? 'wide' : 'default'"
          :event="event"
          @like-changed="
            newVal => {
              handleLikeChanged(i, newVal)
            }
          "
          @show-useful-links="handleUsefulLinks"
        />
      </div>

      <EventsUsefulLinksModal
        :links="usefulLinks"
        @close="
          () => {
            usefulLinks = null
          }
        "
      />

      <EventsReviewModal />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/events/index.scss' as *;
</style>
